/**
 * KoolKonnectPage — the premium creative operating system inside Konnect.
 *
 * Foundation shell (Phase 1-3):
 *   - Dashboard with brand-first cards
 *   - Projects list + New Project wizard
 *   - Brand Profiles + full Reference Kit (logo/colors/voice/products/chars)
 *   - Media Library with retention countdown
 *   - Characters, Products, Scripts, Voices, Music, Avatars — each a full lib
 *   - All AI calls route through Kaharian Gateway (see /api/koolkonnect/*)
 *
 * Phase 4-8 (consistency scoring, movie flow, autonomous planner UI,
 * assembly timeline, Google Drive backup) will layer onto this shell.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Palette, Users, Package, ScrollText, Mic, Music, Film,
  Image as ImageIcon, Video, LayoutGrid, Plus, ArrowRight, Search,
  Upload, Trash2, Star, Loader2, Crown, ArrowUpRight, Wand2, Play,
  FolderOpen, Layers, Clapperboard, X, ChevronRight,
} from 'lucide-react';

import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PROJECT_TYPES = [
  { id: 'social_post', label: 'Social Post', icon: ImageIcon },
  { id: 'static_ad', label: 'Static Ad', icon: LayoutGrid },
  { id: 'ugc_video', label: 'UGC Video', icon: Video },
  { id: 'product_promo', label: 'Product Promo', icon: Package },
  { id: 'explainer_video', label: 'Explainer Video', icon: Play },
  { id: 'avatar_video', label: 'Avatar Video', icon: Users },
  { id: 'brochure', label: 'Brochure', icon: FolderOpen },
  { id: 'campaign_bundle', label: 'Campaign Bundle', icon: Layers },
  { id: 'music_video', label: 'Music Video', icon: Music },
  { id: 'song', label: 'Original Song', icon: Music },
  { id: 'narration', label: 'Narration', icon: Mic },
  { id: 'movie', label: 'Movie', icon: Clapperboard },
  { id: 'long_form_story', label: 'Long-Form Story', icon: Film },
];

// ── Helpers ─────────────────────────────────────────────────────────────────
const daysUntil = (iso) => {
  if (!iso) return null;
  const d = new Date(iso).getTime();
  return Math.max(0, Math.round((d - Date.now()) / (1000 * 60 * 60 * 24)));
};

const RetentionPill = ({ item }) => {
  if (item.keep_forever) return <Badge className="bg-emerald-100 text-emerald-800 border-0 gap-1"><Star className="h-3 w-3" /> Kept</Badge>;
  const d = daysUntil(item.expires_at);
  if (d === null) return null;
  const cls = d <= 3 ? 'bg-rose-100 text-rose-800' : d <= 7 ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700';
  return <Badge className={`${cls} border-0 text-[10px]`}>{d}d</Badge>;
};

// ═══════════════════════════════════════════════════════════════════════════
export default function KoolKonnectPage() {
  const [tab, setTab] = useState('dashboard');
  const [brands, setBrands] = useState([]);
  const [projects, setProjects] = useState([]);
  const [activeBrandId, setActiveBrandId] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [brandFormOpen, setBrandFormOpen] = useState(false);
  const [retention, setRetention] = useState(null);

  const loadAll = async () => {
    try {
      const [b, p, r] = await Promise.all([
        axios.get(`${API}/api/koolkonnect/brands`, auth()),
        axios.get(`${API}/api/koolkonnect/projects`, auth()),
        axios.get(`${API}/api/koolkonnect/retention`, auth()),
      ]);
      setBrands(b.data || []);
      setProjects(p.data || []);
      setRetention(r.data || null);
      if (!activeBrandId && (b.data || []).length > 0) setActiveBrandId(b.data[0].id);
    } catch (e) {
      // silent — user may not have any yet
    }
  };
  useEffect(() => { loadAll(); }, []);

  const activeBrand = brands.find((b) => b.id === activeBrandId);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50" data-testid="koolkonnect-page">
      {/* Hero header */}
      <div className="relative overflow-hidden border-b bg-gradient-to-br from-indigo-950 via-purple-900 to-slate-950 text-white">
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: 'radial-gradient(circle at 20% 10%, rgba(255,255,255,0.15), transparent 40%), radial-gradient(circle at 80% 90%, rgba(139,92,246,0.4), transparent 40%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="h-8 w-8 rounded-lg bg-white/10 backdrop-blur flex items-center justify-center">
                  <Crown className="h-4 w-4 text-amber-300" />
                </div>
                <Badge className="bg-amber-500/90 text-amber-950 border-0 text-[10px] font-semibold">PREMIUM ADD-ON</Badge>
              </div>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
                KoolKonnect <span className="text-amber-300">·</span> <span className="font-light">Creative Studio</span>
              </h1>
              <p className="mt-2 text-sm sm:text-base text-indigo-100 max-w-2xl">
                Premium autonomous creative production — brand-locked, reference-driven, movie-capable. All routed through the Kaharian API Gateway.
              </p>
              {activeBrand && (
                <div className="mt-4 inline-flex items-center gap-2 bg-white/10 backdrop-blur px-3 py-1.5 rounded-full text-xs">
                  {activeBrand.logo_url ? (
                    <img src={`${API}${activeBrand.logo_url}`} alt="" className="h-4 w-4 rounded-full object-cover" />
                  ) : <Palette className="h-3.5 w-3.5" />}
                  Active brand: <strong>{activeBrand.name}</strong>
                  <button onClick={() => setBrandFormOpen(true)} className="ml-2 text-indigo-200 hover:text-white" data-testid="edit-active-brand">Edit</button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setWizardOpen(true)}
                className="bg-white text-indigo-900 hover:bg-amber-100 gap-1.5 font-semibold shadow-lg"
                data-testid="new-project-btn"
              >
                <Sparkles className="h-4 w-4" /> New Project
              </Button>
            </div>
          </div>

          {retention && (
            <div className="mt-6 flex flex-wrap gap-2 text-xs text-indigo-100">
              <span className="bg-white/10 backdrop-blur px-2.5 py-1 rounded-full">📚 {retention.total_assets} assets</span>
              <span className="bg-white/10 backdrop-blur px-2.5 py-1 rounded-full">⭐ {retention.kept_forever} kept forever</span>
              {retention.expiring_within_7_days > 0 && (
                <span className="bg-amber-400/20 backdrop-blur border border-amber-300/30 px-2.5 py-1 rounded-full text-amber-100">⚠️ {retention.expiring_within_7_days} expire within 7 days</span>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="grid grid-cols-3 sm:grid-cols-6 mb-6 h-auto bg-white/70 backdrop-blur border shadow-sm" data-testid="kk-tabs">
            <TabsTrigger value="dashboard" className="gap-1.5 data-[state=active]:bg-indigo-950 data-[state=active]:text-white" data-testid="tab-dashboard"><LayoutGrid className="h-4 w-4" /><span className="hidden sm:inline">Dashboard</span></TabsTrigger>
            <TabsTrigger value="projects" className="gap-1.5 data-[state=active]:bg-indigo-950 data-[state=active]:text-white" data-testid="tab-projects"><Sparkles className="h-4 w-4" /><span className="hidden sm:inline">Projects</span></TabsTrigger>
            <TabsTrigger value="brands" className="gap-1.5 data-[state=active]:bg-indigo-950 data-[state=active]:text-white" data-testid="tab-brands"><Palette className="h-4 w-4" /><span className="hidden sm:inline">Brands</span></TabsTrigger>
            <TabsTrigger value="library" className="gap-1.5 data-[state=active]:bg-indigo-950 data-[state=active]:text-white" data-testid="tab-library"><FolderOpen className="h-4 w-4" /><span className="hidden sm:inline">Library</span></TabsTrigger>
            <TabsTrigger value="assets" className="gap-1.5 data-[state=active]:bg-indigo-950 data-[state=active]:text-white" data-testid="tab-assets"><Layers className="h-4 w-4" /><span className="hidden sm:inline">Assets</span></TabsTrigger>
            <TabsTrigger value="scripts" className="gap-1.5 data-[state=active]:bg-indigo-950 data-[state=active]:text-white" data-testid="tab-scripts"><ScrollText className="h-4 w-4" /><span className="hidden sm:inline">Scripts</span></TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <DashboardTab brands={brands} projects={projects} onNewProject={() => setWizardOpen(true)} onNewBrand={() => setBrandFormOpen(true)} activeBrand={activeBrand} />
          </TabsContent>
          <TabsContent value="projects">
            <ProjectsTab projects={projects} brands={brands} onNew={() => setWizardOpen(true)} onReload={loadAll} />
          </TabsContent>
          <TabsContent value="brands">
            <BrandsTab brands={brands} activeBrandId={activeBrandId} onSelect={setActiveBrandId} onEdit={() => setBrandFormOpen(true)} onReload={loadAll} />
          </TabsContent>
          <TabsContent value="library">
            <MediaLibraryTab brands={brands} />
          </TabsContent>
          <TabsContent value="assets">
            <AssetsTab brands={brands} activeBrandId={activeBrandId} onReload={loadAll} />
          </TabsContent>
          <TabsContent value="scripts">
            <ScriptsTab />
          </TabsContent>
        </Tabs>
      </div>

      <NewProjectWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        brands={brands}
        defaultBrandId={activeBrandId}
        onCreated={() => { loadAll(); setWizardOpen(false); }}
      />
      <BrandFormDialog
        open={brandFormOpen}
        brand={activeBrand}
        onClose={() => setBrandFormOpen(false)}
        onSaved={(b) => { setActiveBrandId(b.id); loadAll(); setBrandFormOpen(false); }}
      />
    </div>
  );
}

// ═══ Dashboard ═══════════════════════════════════════════════════════════════
function DashboardTab({ brands, projects, onNewProject, onNewBrand, activeBrand }) {
  const recent = projects.slice(0, 6);
  return (
    <div className="space-y-6">
      {brands.length === 0 && (
        <Card className="border-dashed border-2 bg-white" data-testid="kk-empty-state">
          <CardContent className="py-12 text-center">
            <Crown className="h-10 w-10 text-amber-500 mx-auto mb-3" />
            <h2 className="text-xl font-semibold text-slate-900">Welcome to KoolKonnect.</h2>
            <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
              Start by creating a Brand Profile — logo, colors, voice, product & character references. Every future project will be locked to it, so your outputs feel like a coherent studio, not raw AI.
            </p>
            <Button className="mt-6 gap-1.5" onClick={onNewBrand} data-testid="dashboard-new-brand-btn">
              <Plus className="h-4 w-4" /> Create Brand Profile
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <QuickCard title="New Project" desc="Brief → autonomous production plan → branded deliverable." icon={Sparkles} onClick={onNewProject} accent="from-indigo-600 to-purple-600" />
        <QuickCard title="Brand Kit" desc={activeBrand ? `Editing ${activeBrand.name}` : 'Set up your brand once, use it everywhere.'} icon={Palette} onClick={onNewBrand} accent="from-emerald-500 to-teal-600" />
        <QuickCard title="Library" desc="Every asset — generated, uploaded, referenced — with retention." icon={FolderOpen} onClick={() => {}} accent="from-amber-500 to-orange-600" />
      </div>

      {recent.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Recent projects</CardTitle>
              <span className="text-xs text-slate-500">{projects.length} total</span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {recent.map((p) => <ProjectCard key={p.id} project={p} brands={brands} />)}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function QuickCard({ title, desc, icon: Icon, onClick, accent }) {
  return (
    <button onClick={onClick} className="text-left group" data-testid={`quick-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`rounded-2xl p-5 bg-gradient-to-br ${accent} text-white shadow-md hover:shadow-xl hover:-translate-y-0.5 transition-all`}>
        <Icon className="h-6 w-6 mb-3 opacity-90" />
        <div className="text-lg font-semibold">{title}</div>
        <p className="text-xs opacity-80 mt-1">{desc}</p>
        <div className="flex items-center gap-1 mt-3 text-xs opacity-90 group-hover:opacity-100">
          Open <ArrowRight className="h-3 w-3" />
        </div>
      </div>
    </button>
  );
}

// ═══ Projects ════════════════════════════════════════════════════════════════
function ProjectsTab({ projects, brands, onNew, onReload }) {
  if (projects.length === 0) {
    return (
      <Card className="border-dashed border-2 bg-white">
        <CardContent className="py-12 text-center">
          <Clapperboard className="h-10 w-10 text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600">No projects yet.</p>
          <Button className="mt-4" onClick={onNew} data-testid="empty-new-project"><Plus className="h-4 w-4 mr-1" /> Start your first project</Button>
        </CardContent>
      </Card>
    );
  }
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
      {projects.map((p) => <ProjectCard key={p.id} project={p} brands={brands} onReload={onReload} />)}
    </div>
  );
}

function ProjectCard({ project, brands }) {
  const brand = brands.find((b) => b.id === project.brand_id);
  const typeMeta = PROJECT_TYPES.find((t) => t.id === project.type) || PROJECT_TYPES[PROJECT_TYPES.length - 1];
  const Icon = typeMeta.icon;
  const statusColor = {
    draft: 'bg-slate-100 text-slate-700',
    planning: 'bg-sky-100 text-sky-800',
    generating: 'bg-purple-100 text-purple-800',
    review: 'bg-amber-100 text-amber-800',
    approved: 'bg-emerald-100 text-emerald-800',
  }[project.status] || 'bg-slate-100';
  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" data-testid={`project-card-${project.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <div className="h-8 w-8 rounded-lg bg-indigo-100 flex items-center justify-center">
                <Icon className="h-4 w-4 text-indigo-700" />
              </div>
              <Badge className={`${statusColor} border-0 text-[10px]`}>{project.status}</Badge>
            </div>
            <h3 className="font-semibold text-sm truncate mt-2">{project.name}</h3>
            <p className="text-[11px] text-slate-500 mt-0.5 truncate">{typeMeta.label} {brand ? `· ${brand.name}` : ''}</p>
            {project.brief && <p className="text-xs text-slate-600 mt-2 line-clamp-2">{project.brief}</p>}
            <div className="text-[10px] text-slate-400 mt-2">{project.output_ids?.length || 0} outputs</div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ═══ Brands ══════════════════════════════════════════════════════════════════
function BrandsTab({ brands, activeBrandId, onSelect, onEdit, onReload }) {
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button onClick={onEdit} data-testid="new-brand-btn"><Plus className="h-4 w-4 mr-1" /> New Brand</Button>
      </div>
      {brands.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="py-10 text-center text-sm text-slate-500">No brand profiles yet.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {brands.map((b) => (
            <Card key={b.id} className={`cursor-pointer transition-shadow ${activeBrandId === b.id ? 'ring-2 ring-indigo-500' : 'hover:shadow-md'}`} onClick={() => onSelect(b.id)} data-testid={`brand-card-${b.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  {b.logo_url ? (
                    <img src={`${API}${b.logo_url}`} alt={b.name} className="h-12 w-12 rounded-lg object-cover bg-slate-100" />
                  ) : (
                    <div className="h-12 w-12 rounded-lg bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold">
                      {b.name?.[0]?.toUpperCase() || 'B'}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{b.name}</div>
                    <div className="text-xs text-slate-500 truncate">{b.tagline || b.industry || 'No tagline'}</div>
                    <div className="flex gap-1 mt-1.5">
                      {(b.colors || []).slice(0, 5).map((c) => (
                        <span key={c} className="h-3.5 w-3.5 rounded-full border border-white shadow-sm" style={{ background: c }} />
                      ))}
                    </div>
                  </div>
                  {activeBrandId === b.id && <Badge className="bg-indigo-100 text-indigo-800 border-0 text-[10px]">Active</Badge>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ Media Library ══════════════════════════════════════════════════════════
function MediaLibraryTab({ brands }) {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);

  const CATS = [
    { id: 'all', label: 'All' },
    { id: 'image', label: 'Images' },
    { id: 'video', label: 'Videos' },
    { id: 'voice', label: 'Voice' },
    { id: 'music', label: 'Music' },
    { id: 'avatar', label: 'Avatars' },
    { id: 'brand', label: 'Brand assets' },
    { id: 'product_ref', label: 'Product refs' },
    { id: 'character_ref', label: 'Character refs' },
    { id: 'external', label: 'External' },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (category !== 'all') params.category = category;
      if (q) params.q = q;
      const { data } = await axios.get(`${API}/api/koolkonnect/library`, { ...auth(), params });
      setItems(data || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [category]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', category === 'all' ? 'external' : category);
    try {
      await axios.post(`${API}/api/koolkonnect/library/upload`, fd, {
        headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Uploaded');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    e.target.value = '';
  };

  const toggleKeep = async (item) => {
    await axios.patch(`${API}/api/koolkonnect/library/${item.id}`, { keep_forever: !item.keep_forever }, auth());
    toast.success(item.keep_forever ? 'No longer kept forever' : 'Kept forever');
    load();
  };

  const del = async (item) => {
    if (!window.confirm('Delete this asset?')) return;
    await axios.delete(`${API}/api/koolkonnect/library/${item.id}`, auth());
    toast.success('Deleted');
    load();
  };

  const filtered = useMemo(() => q
    ? items.filter((i) => i.name?.toLowerCase().includes(q.toLowerCase()))
    : items, [items, q]);

  return (
    <div className="space-y-4" data-testid="library-tab">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {CATS.map((c) => (
            <Button key={c.id} size="sm" variant={category === c.id ? 'default' : 'outline'} onClick={() => setCategory(c.id)} data-testid={`lib-cat-${c.id}`}>{c.label}</Button>
          ))}
        </div>
        <div className="relative ml-auto">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
          <Input placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-56" data-testid="lib-search" />
        </div>
        <label className="inline-flex">
          <Button size="sm" asChild data-testid="lib-upload-btn"><span><Upload className="h-4 w-4 mr-1" /> Upload</span></Button>
          <input type="file" onChange={upload} className="hidden" />
        </label>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading library…</div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="py-12 text-center text-sm text-slate-500">Nothing here yet — upload an asset or generate one from a project.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
          {filtered.map((item) => <LibraryItem key={item.id} item={item} onToggleKeep={() => toggleKeep(item)} onDelete={() => del(item)} />)}
        </div>
      )}
    </div>
  );
}

function LibraryItem({ item, onToggleKeep, onDelete }) {
  const isImage = item.category === 'image' || item.category === 'brand' || item.category === 'product_ref' || item.category === 'character_ref' || (item.meta?.mime || '').startsWith('image/');
  const isVideo = item.category === 'video' || (item.meta?.mime || '').startsWith('video/');
  const isAudio = item.category === 'voice' || item.category === 'music' || (item.meta?.mime || '').startsWith('audio/');
  return (
    <Card className="group relative overflow-hidden">
      <div className="aspect-square bg-slate-100 flex items-center justify-center">
        {isImage && <img src={item.url?.startsWith('http') ? item.url : `${API}${item.url}`} alt={item.name} className="h-full w-full object-cover" />}
        {isVideo && <Video className="h-8 w-8 text-slate-400" />}
        {isAudio && <Music className="h-8 w-8 text-slate-400" />}
        {!isImage && !isVideo && !isAudio && <FolderOpen className="h-8 w-8 text-slate-400" />}
      </div>
      <div className="absolute top-1 right-1 flex flex-col gap-1">
        <RetentionPill item={item} />
      </div>
      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="text-white text-xs font-medium truncate">{item.name}</div>
        <div className="flex justify-between items-center mt-1">
          <button onClick={onToggleKeep} className="text-xs text-amber-300 hover:text-amber-200" data-testid={`lib-keep-${item.id}`}>
            {item.keep_forever ? 'Unkeep' : 'Keep'}
          </button>
          <button onClick={onDelete} className="text-xs text-rose-300 hover:text-rose-200" data-testid={`lib-delete-${item.id}`}>Delete</button>
        </div>
      </div>
    </Card>
  );
}

// ═══ Assets (Characters + Products + Voices + Music + Avatars) ═══════════════
function AssetsTab({ brands, activeBrandId, onReload }) {
  const [sub, setSub] = useState('characters');
  const SUBS = [
    { id: 'characters', label: 'Characters', icon: Users },
    { id: 'products', label: 'Products', icon: Package },
    { id: 'voices', label: 'Voices', icon: Mic },
    { id: 'music', label: 'Music', icon: Music },
    { id: 'avatars', label: 'Avatars', icon: Film },
  ];
  return (
    <div className="space-y-4" data-testid="assets-tab">
      <div className="flex flex-wrap gap-1">
        {SUBS.map((s) => (
          <Button key={s.id} size="sm" variant={sub === s.id ? 'default' : 'outline'} onClick={() => setSub(s.id)} className="gap-1" data-testid={`assets-sub-${s.id}`}>
            <s.icon className="h-3.5 w-3.5" /> {s.label}
          </Button>
        ))}
      </div>
      {sub === 'characters' && <CharactersSection brands={brands} activeBrandId={activeBrandId} />}
      {sub === 'products' && <ProductsSection brands={brands} activeBrandId={activeBrandId} />}
      {sub === 'voices' && <SimpleUploadSection endpoint="voices" categoryLabel="voice sample" acceptType="audio/*" />}
      {sub === 'music' && <SimpleUploadSection endpoint="music" categoryLabel="music track" acceptType="audio/*" />}
      {sub === 'avatars' && <SimpleUploadSection endpoint="avatars" categoryLabel="avatar source" acceptType="image/*,video/*" />}
    </div>
  );
}

function CharactersSection({ brands, activeBrandId }) {
  const [chars, setChars] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', gender: '', age_range: '' });
  const load = async () => {
    const { data } = await axios.get(`${API}/api/koolkonnect/characters`, auth());
    setChars(data || []);
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    await axios.post(`${API}/api/koolkonnect/characters`, { ...form, brand_id: activeBrandId }, auth());
    toast.success('Character added');
    setCreating(false); setForm({ name: '', description: '', gender: '', age_range: '' });
    load();
  };
  const uploadRef = async (cid, angle, file) => {
    const fd = new FormData();
    fd.append('file', file); fd.append('angle', angle);
    await axios.post(`${API}/api/koolkonnect/characters/${cid}/references`, fd, { headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } });
    toast.success('Reference added');
    load();
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setCreating(true)} data-testid="new-character-btn"><Plus className="h-4 w-4 mr-1" /> New Character</Button>
      </div>
      {chars.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="py-10 text-center text-sm text-slate-500">No characters yet. Add one to lock a person&apos;s identity across all outputs.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {chars.map((c) => <CharacterCard key={c.id} character={c} onUploadRef={uploadRef} />)}
        </div>
      )}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Character</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Founder Anna" data-testid="char-name" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Personality, style, notes…" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Gender</Label><Input value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })} /></div>
              <div><Label>Age range</Label><Input value={form.age_range} onChange={(e) => setForm({ ...form, age_range: e.target.value })} placeholder="e.g. 25-35" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-char-btn">Add character</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CharacterCard({ character, onUploadRef }) {
  const ANGLES = ['front', 'left', 'right', 'back', 'three_quarter'];
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-12 w-12 rounded-lg bg-purple-100 flex items-center justify-center">
            <Users className="h-5 w-5 text-purple-700" />
          </div>
          <div>
            <div className="font-semibold">{character.name}</div>
            <div className="text-xs text-slate-500">{[character.gender, character.age_range].filter(Boolean).join(' · ')}</div>
          </div>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {ANGLES.map((a) => {
            const url = character.reference_angles?.[a];
            return (
              <label key={a} className="aspect-square rounded-lg bg-slate-100 flex items-center justify-center text-[10px] text-slate-500 hover:bg-slate-200 cursor-pointer overflow-hidden relative" data-testid={`char-angle-${character.id}-${a}`}>
                {url ? (
                  <img src={`${API}${url}`} alt={a} className="h-full w-full object-cover" />
                ) : (
                  <div className="text-center p-1"><Upload className="h-3 w-3 mx-auto mb-0.5" />{a}</div>
                )}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && onUploadRef(character.id, a, e.target.files[0])} />
              </label>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function ProductsSection({ brands, activeBrandId }) {
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', category: '' });
  const load = async () => {
    const { data } = await axios.get(`${API}/api/koolkonnect/products`, auth());
    setItems(data || []);
  };
  useEffect(() => { load(); }, []);
  const create = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    await axios.post(`${API}/api/koolkonnect/products`, { ...form, brand_id: activeBrandId }, auth());
    toast.success('Product added'); setCreating(false); setForm({ name: '', description: '', category: '' }); load();
  };
  const uploadRef = async (pid, angle, file) => {
    const fd = new FormData();
    fd.append('file', file); fd.append('angle', angle);
    await axios.post(`${API}/api/koolkonnect/products/${pid}/references`, fd, { headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } });
    toast.success('Reference added'); load();
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" onClick={() => setCreating(true)} data-testid="new-product-btn"><Plus className="h-4 w-4 mr-1" /> New Product</Button>
      </div>
      {items.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="py-10 text-center text-sm text-slate-500">No products yet. Add one to lock its identity across all creative outputs.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {items.map((p) => (
            <Card key={p.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center"><Package className="h-5 w-5 text-emerald-700" /></div>
                  <div>
                    <div className="font-semibold">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.category || '—'}</div>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-1.5">
                  {['front', 'back', 'left', 'right', 'detail'].map((a) => {
                    const url = p.reference_angles?.[a];
                    return (
                      <label key={a} className="aspect-square rounded-lg bg-slate-100 flex items-center justify-center text-[10px] text-slate-500 hover:bg-slate-200 cursor-pointer overflow-hidden relative">
                        {url ? <img src={`${API}${url}`} alt={a} className="h-full w-full object-cover" /> : <div className="text-center p-1"><Upload className="h-3 w-3 mx-auto mb-0.5" />{a}</div>}
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadRef(p.id, a, e.target.files[0])} />
                      </label>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Product</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name" /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g. Smart ring, beverage bottle" /></div>
            <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={create} data-testid="save-product-btn">Add product</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SimpleUploadSection({ endpoint, categoryLabel, acceptType }) {
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const load = async () => {
    const { data } = await axios.get(`${API}/api/koolkonnect/${endpoint}`, auth());
    setItems(data || []);
  };
  useEffect(() => { load(); }, [endpoint]);
  const doUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    fd.append('name', file.name);
    try {
      await axios.post(`${API}/api/koolkonnect/${endpoint}/upload`, fd, { headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } });
      toast.success('Uploaded');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); e.target.value = ''; }
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <label>
          <Button size="sm" asChild disabled={uploading}><span>{uploading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Uploading…</> : <><Upload className="h-4 w-4 mr-1" /> Upload {categoryLabel}</>}</span></Button>
          <input type="file" accept={acceptType} onChange={doUpload} className="hidden" />
        </label>
      </div>
      {items.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="py-10 text-center text-sm text-slate-500">No {categoryLabel}s yet.</CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((i) => (
            <Card key={i.id}>
              <CardContent className="p-4 flex items-center gap-3">
                {(acceptType.includes('audio')) ? <Mic className="h-6 w-6 text-slate-500" /> : <Film className="h-6 w-6 text-slate-500" />}
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm truncate">{i.name}</div>
                  <div className="text-xs text-slate-500">{i.status || i.mood || i.kind || ''}</div>
                  {(i.sample_url || i.url || i.source_url) && (
                    <audio controls src={`${API}${i.sample_url || i.url || i.source_url}`} className="mt-2 w-full h-8" />
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══ Scripts ═════════════════════════════════════════════════════════════════
function ScriptsTab() {
  const [scripts, setScripts] = useState([]);
  const [genOpen, setGenOpen] = useState(false);
  const [form, setForm] = useState({ title: '', kind: 'script', brief: '' });
  const [busy, setBusy] = useState(false);
  const load = async () => {
    const { data } = await axios.get(`${API}/api/koolkonnect/scripts`, auth());
    setScripts(data || []);
  };
  useEffect(() => { load(); }, []);
  const generate = async () => {
    if (!form.brief.trim()) { toast.error('Brief required'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/api/koolkonnect/scripts/generate`, form, auth());
      toast.success('Script generated via Kaharian Gateway');
      setGenOpen(false); setForm({ title: '', kind: 'script', brief: '' }); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Generation failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2">
        <Button size="sm" onClick={() => setGenOpen(true)} data-testid="new-script-btn"><Wand2 className="h-4 w-4 mr-1" /> Generate with AI</Button>
      </div>
      {scripts.length === 0 ? (
        <Card className="border-dashed border-2"><CardContent className="py-10 text-center text-sm text-slate-500">No scripts yet.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {scripts.map((s) => (
            <Card key={s.id}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <ScrollText className="h-4 w-4 text-slate-500" />
                  <span className="font-semibold text-sm">{s.title}</span>
                  <Badge className="bg-slate-100 text-slate-700 border-0 text-[10px]">{s.kind}</Badge>
                  {s.generated && <Badge className="bg-purple-100 text-purple-800 border-0 text-[10px]">AI</Badge>}
                </div>
                <pre className="text-xs whitespace-pre-wrap text-slate-700 mt-2 max-h-64 overflow-y-auto bg-slate-50 p-3 rounded">{s.content}</pre>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={genOpen} onOpenChange={setGenOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generate Script</DialogTitle>
            <DialogDescription>Routed through Kaharian API Gateway (Kie.ai primary → direct providers as fallback).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Kind</Label>
              <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                <SelectTrigger data-testid="script-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="script">Script</SelectItem>
                  <SelectItem value="outline">Outline</SelectItem>
                  <SelectItem value="treatment">Treatment</SelectItem>
                  <SelectItem value="dialogue">Dialogue draft</SelectItem>
                  <SelectItem value="shot_list">Shot list</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Title (optional)</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
            <div>
              <Label>Brief</Label>
              <Textarea rows={5} value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} placeholder="Describe what you want. Be specific about tone, characters, goals, platform." data-testid="script-brief" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenOpen(false)}>Cancel</Button>
            <Button onClick={generate} disabled={busy} data-testid="generate-script-btn">{busy ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Generating…</> : <><Wand2 className="h-4 w-4 mr-1" /> Generate</>}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══ New Project Wizard ═════════════════════════════════════════════════════
function NewProjectWizard({ open, onClose, brands, defaultBrandId, onCreated }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ type: 'social_post', brand_id: defaultBrandId || '', name: '', brief: '', target_platforms: [], aspect_ratios: ['1:1'] });
  useEffect(() => { if (open) setForm((f) => ({ ...f, brand_id: defaultBrandId || '' })); }, [defaultBrandId, open]);
  useEffect(() => { if (!open) setStep(1); }, [open]);

  const create = async () => {
    if (!form.name.trim()) { toast.error('Project name required'); return; }
    try {
      const { data } = await axios.post(`${API}/api/koolkonnect/projects`, form, auth());
      toast.success('Project created');
      onCreated?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Create failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="new-project-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-amber-500" /> New Project</DialogTitle>
          <DialogDescription>Step {step} of 3 — {step === 1 ? 'Type' : step === 2 ? 'Brand & scope' : 'Brief'}</DialogDescription>
        </DialogHeader>

        {step === 1 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {PROJECT_TYPES.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.id}
                  onClick={() => setForm({ ...form, type: t.id })}
                  className={`p-4 rounded-xl border-2 text-left transition-all ${form.type === t.id ? 'border-indigo-500 bg-indigo-50' : 'border-slate-200 hover:border-slate-300'}`}
                  data-testid={`ptype-${t.id}`}
                >
                  <Icon className={`h-5 w-5 mb-2 ${form.type === t.id ? 'text-indigo-600' : 'text-slate-500'}`} />
                  <div className="text-sm font-medium">{t.label}</div>
                </button>
              );
            })}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-3">
            <div>
              <Label>Brand profile</Label>
              <Select value={form.brand_id || ''} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                <SelectTrigger data-testid="wizard-brand"><SelectValue placeholder="(optional — no brand lock)" /></SelectTrigger>
                <SelectContent>
                  {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Project name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Smart Ring · Launch Campaign" data-testid="wizard-name" />
            </div>
            <div>
              <Label>Aspect ratios</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {['1:1', '9:16', '16:9', '4:5', '3:4'].map((r) => (
                  <button
                    key={r}
                    onClick={() => setForm({ ...form, aspect_ratios: form.aspect_ratios.includes(r) ? form.aspect_ratios.filter((x) => x !== r) : [...form.aspect_ratios, r] })}
                    className={`px-3 py-1 text-xs rounded-full border ${form.aspect_ratios.includes(r) ? 'bg-indigo-500 text-white border-indigo-500' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                  >{r}</button>
                ))}
              </div>
            </div>
            <div>
              <Label>Target platforms</Label>
              <div className="flex flex-wrap gap-1.5 mt-1">
                {['instagram', 'tiktok', 'youtube', 'facebook', 'linkedin', 'website'].map((p) => (
                  <button
                    key={p}
                    onClick={() => setForm({ ...form, target_platforms: form.target_platforms.includes(p) ? form.target_platforms.filter((x) => x !== p) : [...form.target_platforms, p] })}
                    className={`px-3 py-1 text-xs rounded-full border capitalize ${form.target_platforms.includes(p) ? 'bg-purple-500 text-white border-purple-500' : 'bg-white border-slate-200 hover:border-slate-300'}`}
                  >{p}</button>
                ))}
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            <div>
              <Label>Creative brief</Label>
              <Textarea rows={8} value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} placeholder="Describe the project. Who is it for? What's the goal? What tone? Include any specific must-haves." data-testid="wizard-brief" />
              <p className="text-[11px] text-slate-500 mt-1">The Planning Agent will turn this brief into a detailed production plan using your Brand Profile references.</p>
            </div>
          </div>
        )}

        <DialogFooter>
          {step > 1 && <Button variant="outline" onClick={() => setStep(step - 1)}>Back</Button>}
          {step < 3 ? (
            <Button onClick={() => setStep(step + 1)} data-testid="wizard-next">Next <ChevronRight className="h-4 w-4 ml-1" /></Button>
          ) : (
            <Button onClick={create} className="bg-indigo-600 hover:bg-indigo-700" data-testid="wizard-create"><Sparkles className="h-4 w-4 mr-1" /> Create project</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══ Brand Form Dialog ══════════════════════════════════════════════════════
function BrandFormDialog({ open, brand, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: '', tagline: '', description: '', industry: '', target_audience: '',
    positioning: '', voice_tone: '', colors: [], logo_url: '',
  });
  const [logoFile, setLogoFile] = useState(null);
  useEffect(() => {
    if (open) {
      setForm(brand ? {
        name: brand.name || '', tagline: brand.tagline || '', description: brand.description || '',
        industry: brand.industry || '', target_audience: brand.target_audience || '',
        positioning: brand.positioning || '', voice_tone: brand.voice_tone || '',
        colors: brand.colors || [], logo_url: brand.logo_url || '',
      } : { name: '', tagline: '', description: '', industry: '', target_audience: '', positioning: '', voice_tone: '', colors: [], logo_url: '' });
      setLogoFile(null);
    }
  }, [brand, open]);

  const save = async () => {
    if (!form.name.trim()) { toast.error('Brand name required'); return; }
    try {
      let result;
      if (brand?.id) {
        await axios.patch(`${API}/api/koolkonnect/brands/${brand.id}`, form, auth());
        result = { id: brand.id };
      } else {
        const { data } = await axios.post(`${API}/api/koolkonnect/brands`, form, auth());
        result = data;
      }
      if (logoFile && result.id) {
        const fd = new FormData(); fd.append('file', logoFile);
        await axios.post(`${API}/api/koolkonnect/brands/${result.id}/logo`, fd, { headers: { ...auth().headers, 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Brand saved');
      onSaved?.(result);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const addColor = () => setForm({ ...form, colors: [...form.colors, '#6366f1'] });
  const updateColor = (i, v) => { const cs = [...form.colors]; cs[i] = v; setForm({ ...form, colors: cs }); };
  const removeColor = (i) => setForm({ ...form, colors: form.colors.filter((_, idx) => idx !== i) });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="brand-form-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Palette className="h-5 w-5" /> {brand?.id ? 'Edit brand' : 'New brand profile'}</DialogTitle>
          <DialogDescription>Upload once, reuse across every future project.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="brand-name" /></div>
          <div><Label>Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></div>
          <div><Label>Industry</Label><Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} /></div>
          <div><Label>Target audience</Label><Input value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} /></div>
          <div className="md:col-span-2"><Label>Positioning</Label><Textarea rows={2} value={form.positioning} onChange={(e) => setForm({ ...form, positioning: e.target.value })} /></div>
          <div className="md:col-span-2"><Label>Voice & tone guide</Label><Textarea rows={3} value={form.voice_tone} onChange={(e) => setForm({ ...form, voice_tone: e.target.value })} placeholder="e.g. Warm, confident, minimalist. Never salesy. Use short sentences." /></div>
          <div className="md:col-span-2">
            <Label>Brand colors</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {form.colors.map((c, i) => (
                <div key={i} className="flex items-center gap-1 bg-slate-50 border rounded px-1.5 py-1">
                  <input type="color" value={c} onChange={(e) => updateColor(i, e.target.value)} className="h-6 w-6 rounded cursor-pointer" />
                  <input value={c} onChange={(e) => updateColor(i, e.target.value)} className="w-16 text-xs bg-transparent border-0 focus:outline-none" />
                  <button onClick={() => removeColor(i)} className="text-slate-400 hover:text-rose-500"><X className="h-3 w-3" /></button>
                </div>
              ))}
              <button onClick={addColor} className="px-2 py-1 text-xs border rounded border-dashed hover:border-slate-400">+ Add color</button>
            </div>
          </div>
          <div className="md:col-span-2">
            <Label>Logo</Label>
            <div className="flex items-center gap-3 mt-1">
              {form.logo_url && <img src={`${API}${form.logo_url}`} alt="" className="h-12 w-12 rounded bg-slate-100 object-cover" />}
              <label className="cursor-pointer">
                <Button variant="outline" size="sm" asChild><span><Upload className="h-3.5 w-3.5 mr-1" /> {form.logo_url ? 'Replace logo' : 'Upload logo'}</span></Button>
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setLogoFile(e.target.files?.[0] || null)} />
              </label>
              {logoFile && <span className="text-xs text-slate-500">{logoFile.name}</span>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-brand-btn">Save brand</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
